import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, Trophy, Plus, Trash2, ArrowLeft } from "lucide-react";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";

export default function MyFighters() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fighters, setFighters] = useState([]);
  const [slotsUsed, setSlotsUsed] = useState(0);
  const [slotsTotal, setSlotsTotal] = useState(1);
  const [season, setSeason] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadFighters();
  }, []);

  const loadFighters = async () => {
    try {
      const { data } = await api.get("/arena/fighters");
      setFighters(data.fighters);
      setSlotsUsed(data.slots_used);
      setSlotsTotal(data.slots_total);
      setSeason(data.season);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    
    setBusy(true);
    try {
      const { data } = await api.delete(`/arena/fighters/${deleteModal.id}`);
      toast.success(data.message);
      setDeleteModal(null);
      await loadFighters();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <PageHeader
            icon={Swords}
            title="My Gladiators"
            subtitle={`${season?.name || "Season 1"} - ${slotsUsed} / ${slotsTotal} slots`}
          />
        </div>
        {slotsUsed < slotsTotal && (
          <button
            onClick={() => navigate("/arena-circuit/create-fighter")}
            className={btn() + " flex items-center gap-2"}
          >
            <Plus size={16} />
            Create Gladiator
          </button>
        )}
      </div>

      {fighters.length === 0 ? (
        <Empty label="No Gladiators">
          <p className="text-sm text-zinc-400 mb-4">
            You haven&apos;t created any Arena Gladiators this season
          </p>
          <button
            onClick={() => navigate("/arena-circuit/create-fighter")}
            className={btn()}
          >
            <Plus size={16} className="inline mr-2" />
            Create Your First Gladiator
          </button>
        </Empty>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fighters.map((fighter) => (
            <div
              key={fighter.id}
              className="border border-amber-900/30 bg-zinc-950 p-5 hover:border-amber-600/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-cinzel text-lg text-amber-300 tracking-wide mb-1">
                    {fighter.name}
                  </h3>
                  <p className="text-xs text-zinc-500 capitalize mb-2">{fighter.cls}</p>
                  
                  <div className="flex flex-wrap gap-2 text-[10px] mb-3">
                    <span className="px-2 py-0.5 bg-amber-900/20 border border-amber-700/40 text-amber-300 rounded-sm">
                      {fighter.class_specialist.replace(/_/g, " ")}
                    </span>
                    <span className="px-2 py-0.5 bg-amber-900/20 border border-amber-700/40 text-amber-300 rounded-sm capitalize">
                      {fighter.tactic_specialist}
                    </span>
                  </div>
                </div>
                {fighter.corona_holder && (
                  <Trophy size={24} className="text-amber-400" title="Corona Holder" />
                )}
              </div>

              <div className="flex items-center gap-4 text-xs text-zinc-400 mb-3 pb-3 border-b border-zinc-800">
                <span className="flex items-center gap-1">
                  <Trophy size={12} />
                  {fighter.record.wins}W / {fighter.record.losses}L
                </span>
                <span className="text-zinc-600">|</span>
                <span>{fighter.record.bouts} Bouts</span>
              </div>

              {fighter.current_contract ? (
                <div className="text-xs text-emerald-400 mb-3">
                  ⚔️ Under Contract
                </div>
              ) : (
                <div className="text-xs text-zinc-500 mb-3">
                  Free Agent
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/arena-circuit/fighter/${fighter.id}`)}
                  className={btn() + " flex-1 text-sm"}
                >
                  View Profile
                </button>
                {!fighter.current_contract && (
                  <button
                    onClick={() => setDeleteModal(fighter)}
                    className="px-3 py-2 bg-red-900/20 border border-red-700/40 text-red-300 hover:bg-red-900/30 transition-colors text-sm"
                    title="Retire Gladiator"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <Modal open={!!deleteModal} onClose={() => setDeleteModal(null)} title="Retire Fighter">
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">
              Are you sure you want to retire <span className="text-amber-300 font-cinzel font-semibold">{deleteModal.name}</span>?
            </p>
            <div className="bg-red-950/20 border border-red-700/40 rounded p-3">
              <p className="text-xs text-red-300">
                ⚠️ This action cannot be undone. The gladiator will be permanently deleted.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal(null)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className={btn() + " flex-1 bg-red-600 hover:bg-red-500"}
                disabled={busy}
              >
                {busy ? "Retiring..." : "Retire Gladiator"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
